import { formatDateWithTz } from 'Helpers/dateUtils';
import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

export const DASH = '—';

/**
 * A raw UTC `Y-m-d H:i:s` from the server, shown in the browser's timezone. Every
 * pop-up date that names when a request was raised or decided goes through these,
 * so the explanation, the history and "Requested by" cannot disagree on the day.
 */
export const localDate = (utc) => (utc ? formatDateWithTz(utc, 'YYYY-MM-DD HH:mm:ss', 'D MMM YYYY') : DASH);

export const localDateTime = (utc) => (utc ? formatDateWithTz(utc, 'YYYY-MM-DD HH:mm:ss', 'D MMM YYYY, h:mm a') : DASH);

export const STATUS_PENDING = 0;
export const STATUS_APPROVED = 1;
export const STATUS_REJECTED = 2;
export const STATUS_APPLIED = 4;

export const DIRECTION_RETURNED = 'returned';

/** A location's log Type for setting its contingency, which draws on no source of funds. */
export const TYPE_CONTINGENCY_ALLOCATION = 'contingency_allocation';
export const CATEGORY_REVENUE = 'revenue';

/**
 * Money in this report is shown in whole units, negatives in brackets. The server
 * keeps the cents, so the CSV export still carries them.
 */
export const money = (value) => formatToFixedMoneyWithLocale(Number(value ?? 0), 0);

/** A balance that has never been activated is a dash, not a zero. */
export const balance = (value) => (value === null || value === undefined ? DASH : money(value));

/**
 * Money in a table cell: as `money`, but a zero is the dash and nothing entered is
 * blank. Figures inside a sentence keep `money` / `balance`.
 */
export const figure = (value) => formatFigure(value, formatToFixedMoneyWithLocale, { precision: 0 });

/**
 * A row's From or To, as `figure`. A contingency allocation's 0 is the pot's balance,
 * so it reads 0 rather than the dash; a From it never had stays blank.
 */
export const lineAmount = (row, value) =>
    row.type === TYPE_CONTINGENCY_ALLOCATION && value !== null && value !== undefined && Number(value) === 0 ? money(0) : figure(value);

export const statusChipClass = (status) => {
    if (status === STATUS_APPROVED || status === STATUS_APPLIED) {
        return 'bc-chip bc-chip--green';
    }

    if (status === STATUS_REJECTED) {
        return 'bc-chip bc-chip--grey';
    }

    return 'bc-chip bc-chip--amber';
};

/**
 * The funding sources a request actually draws on, in the order the engine draws
 * them. Derived from the one calculated split the server sends, so the table row,
 * the pop-up legend and the CSV can never name different sources for one request.
 *
 * Each amount is shown in whole units, so the rounded total is shared out by largest
 * remainder: every source takes its whole units, and the units left over go one each
 * to the sources with the largest fractions. The parts shown then add up to the
 * change exactly. A source left with nothing is not named.
 *
 * A revenue increase brings in money no line drew, so what it puts into Event
 * profit is `added` rather than `returned`.
 */
export const fundingSources = (funding, category = null) => {
    if (!funding) {
        return [];
    }

    const added = category === CATEGORY_REVENUE && funding.direction === DIRECTION_RETURNED;

    const sources = [
        { key: 'local', label: $t('Local contingency'), amount: Number(funding.local ?? 0) },
        { key: 'central', label: $t('Central contingency'), amount: Number(funding.central ?? 0) },
        { key: 'profit', label: $t('Event profit'), amount: Number(funding.profit ?? 0) },
    ].filter((source) => source.amount > 0);

    const whole = sources.map((source) => Math.floor(source.amount));
    const leftOver = Math.round(sources.reduce((sum, source) => sum + source.amount, 0)) - whole.reduce((sum, part) => sum + part, 0);

    sources
        .map((source, index) => ({ index, fraction: source.amount - whole[index] }))
        .sort((a, b) => b.fraction - a.fraction || a.index - b.index)
        .slice(0, leftOver)
        .forEach(({ index }) => {
            whole[index] += 1;
        });

    return sources
        .map((source, index) => ({ ...source, returned: funding.direction === DIRECTION_RETURNED && !added, added, amount: whole[index] }))
        .filter((source) => source.amount > 0);
};

/**
 * Why a pending approval level applies. The escalation value is read from current
 * configuration and is absent once that configuration no longer describes this
 * request's chain, so the sentence has to work without it.
 *
 * Level 1 gets none: it is the base approver, there is no level below it to read a
 * threshold from, and "Required — additional approval level" against it reads as
 * though the first approver needed justifying.
 */
export const escalationReason = (level) => {
    if (level.level <= 1) {
        return null;
    }

    return level.escalation_value === null || level.escalation_value === undefined
        ? $t('Required — additional approval level')
        : $t('Required — change exceeds the Level {{level}} escalation value ({{value}})', {
              level: level.level,
              value: money(level.escalation_value),
          });
};
